'use client';

import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, TrendingDown, Archive } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface InvKpi {
  total: number;
  inStock: number;
  lowStock: number;
  outOfStock: number;
}

export default function InventoryHeader() {
  const [kpi, setKpi] = useState<InvKpi>({ total: 0, inStock: 0, lowStock: 0, outOfStock: 0 });
  const [loading, setLoading] = useState(true);

  const fetchKpi = async () => {
    const supabase = createClient();
    const { data, error } = await supabase.from('parts').select('stock, min_stock');
    if (error) { setLoading(false); return; }
    const parts = data || [];
    const total = parts.length;
    const outOfStock = parts.filter((p) => (p.stock as number) === 0).length;
    const lowStock = parts.filter((p) => (p.stock as number) > 0 && (p.stock as number) < (p.min_stock as number)).length;
    const inStock = total - outOfStock - lowStock;
    setKpi({ total, inStock, lowStock, outOfStock });
    setLoading(false);
  };

  useEffect(() => {
    fetchKpi();
    const supabase = createClient();
    const channel = supabase
      .channel('inv_header_parts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parts' }, () => fetchKpi())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const kpis = [
    { label: 'Total Parts', value: loading ? '…' : String(kpi.total), icon: Archive, variant: 'default' },
    { label: 'In Stock', value: loading ? '…' : String(kpi.inStock), icon: Package, variant: 'positive' },
    { label: 'Low Stock', value: loading ? '…' : String(kpi.lowStock), icon: AlertTriangle, variant: 'warning' },
    { label: 'Out of Stock', value: loading ? '…' : String(kpi.outOfStock), icon: TrendingDown, variant: 'danger' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Inventory Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-2">
            Parts warehouse — All Branches
            <span className="flex items-center gap-1 text-xs text-positive">
              <span className="w-1.5 h-1.5 rounded-full bg-positive inline-block alert-pulse" />
              Live
            </span>
          </p>
        </div>
      </div>
      {/* Inventory KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4">
        {kpis.map((kpiItem) => (
          <div
            key={`inv-kpi-${kpiItem.label}`}
            className={`card-surface p-3 lg:p-4 flex items-center gap-3 ${
              kpiItem.variant === 'warning' ? 'border-warning/20' :
              kpiItem.variant === 'danger' ? 'border-danger/20' :
              kpiItem.variant === 'positive' ? 'border-positive/20' : ''
            }`}
          >
            <div className={`w-8 h-8 lg:w-9 lg:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              kpiItem.variant === 'warning' ? 'bg-warning/10 text-warning' :
              kpiItem.variant === 'danger' ? 'bg-danger/10 text-danger' :
              kpiItem.variant === 'positive' ? 'bg-positive/10 text-positive' : 'bg-primary/10 text-primary'
            }`}>
              <kpiItem.icon size={18} />
            </div>
            <div>
              <p className="font-mono-data text-lg lg:text-xl font-bold text-foreground">{kpiItem.value}</p>
              <p className="text-muted-foreground uppercase tracking-widest" style={{ fontSize: '10px' }}>{kpiItem.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}